import { useState, useEffect, useReducer } from "react";
import { get } from "aws-amplify/api";
import currentUserToken from "CurrentUserToken";
import { useLocation, useNavigate } from "react-router-dom";
import PropTypes from "prop-types";
import AppBar from "@mui/material/AppBar";
import Toolbar from "@mui/material/Toolbar";
import IconButton from "@mui/material/IconButton";
import Menu from "@mui/material/Menu";
import Icon from "@mui/material/Icon";
import Switch from "@mui/material/Switch";
import {
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Tooltip,
  Drawer,
  Badge,
} from "@mui/material";
import LanguageIcon from "@mui/icons-material/Language";
import Breadcrumbs from "examples/Breadcrumbs";
import { signOut } from "aws-amplify/auth";
import PersonIcon from "@mui/icons-material/Person";
import {
  useTemplateController,
  setTransparentNavbar,
  setMiniSidenav,
  setDarkSidenav,
  setDarkMode,
} from "context";
import TemplateBox from "components/TemplateBox";
import TemplateTypography from "components/TemplateTypography";
import {
  navbar,
  navbarContainer,
  navbarRow,
  navbarIconButton,
} from "examples/Navbars/DashboardNavbar/styles";
import { I18n } from "@aws-amplify/core";
import { Box, Stack } from "@mui/material";
import dayjs from "dayjs";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import Grid from "@mui/material/Grid";
import MonitorIcon from "@mui/icons-material/Monitor";
import AccountCircleIcon from "@mui/icons-material/AccountCircle";
import WarningIcon from "@mui/icons-material/Warning";
import StorefrontIcon from "@mui/icons-material/Storefront";
import SettingsIcon from "@mui/icons-material/Settings";
import breakpoints from "assets/theme/base/breakpoints";
import { fetchAuthSession } from "aws-amplify/auth";
import { stringAvatar } from "assets/util-functions";
import Avatar from "@mui/material/Avatar";
import MailOutlineIcon from "@mui/icons-material/MailOutline";
import GroupOutlinedIcon from "@mui/icons-material/GroupOutlined";
import { stringToColor } from "assets/util-functions";
import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import brand from "assets/images/logos/LOGO-EptaService_3D_CMYK_white.png";
import NotificationsNoneIcon from '@mui/icons-material/NotificationsNone';
import NotificationsIcon from '@mui/icons-material/Notifications';

const reducerLanguage = (lastState, action) => {
  if (action.type === "DATA_RECEIVED") {
    const audit = action.value;
    if (Array.isArray(audit)) {
      audit.forEach((element) => {
        if (typeof element.timestamp === "number") {
          element.timestamp = dayjs(element.timestamp).format(
            "YYYY-MM-DD HH:mm"
          );
        }
      });
      return { audit: audit, fetched: true };
    }
  }
  return lastState;
};

function DashboardNavbar({
  absolute,
  light,
  isMini,
  toggleDarkMode,
  language,
  setLanguage,
  profile,
  setProfile,
  refreshLanguages,
}) {
  const navigate = useNavigate();
  const theme = useTheme();
  const mobileDevice = !useMediaQuery(theme.breakpoints.up("md"));
  const [tabsOrientation, setTabsOrientation] = useState("horizontal");
  const [tabValue, setTabValue] = useState(0);
  const [userInfo, setUserInfo] = useState({
    email: "",
    name: "",
    profiles: ["NOT ADMIN"],
  });
  const [stateLanguage, dispatchLanguage] = useReducer(reducerLanguage, {
    audit: [],
    fetched: false,
  });
  const [userIdS, setUserIds] = useState("");
  const tlmGetUserGroups = async (userID) => {
    try {
      const restOperation = get({
        apiName: "TelemonitoringAPI",
        path: "/userManagement/user/groups",
        options: {
          headers: {
            "Content-Type": "application/json",
            Authorization: `${await currentUserToken()}`,
          },
          queryParams: { user_id: userID },
        },
      });
      const { body } = await restOperation.response;
      const response = await body.json();

      // Extract profiles from the response and set groupNameSel with the first profile
      const profiles = response.profiles.map((profile) => profile.group_name);
      return profiles;
    } catch (error) {
      console.error("Error fetching user groups:", error);
    }
  };
  useEffect(() => {
    async function currentSession() {
      try {
        const { accessToken, idToken } =
          (await fetchAuthSession()).tokens ?? {};
        const profiles = await tlmGetUserGroups(
          idToken.payload["cognito:username"]
        );

        setUserInfo({
          name: idToken.payload.name || "No Name",
          email: idToken.payload.email || "No email",
          profiles: profiles || [],
        });
        // Save profiles to setProfile
        setProfile(profiles);
      } catch (err) {}
    }

    currentSession();
  }, []);

  useEffect(() => {
    // A function that sets the orientation state of the tabs.
    function handleTabsOrientation() {
      return window.innerWidth < breakpoints.values.sm
        ? setTabsOrientation("vertical")
        : setTabsOrientation("horizontal");
    }
    window.addEventListener("resize", handleTabsOrientation);
    handleTabsOrientation();
    return () => window.removeEventListener("resize", handleTabsOrientation);
  }, [tabsOrientation]);

  const [languages, setLanguages] = useState([]);
  const [navbarType, setNavbarType] = useState();
  const [controller, dispatch] = useTemplateController();
  const { transparentNavbar, fixedNavbar, darkMode } = controller;
  const [drawerOpen, setDrawerOpen] = useState(false); // Manage drawer state

  const handleDrawerToggle = () => {
    setDrawerOpen(!drawerOpen);
  };
  const [openSettings, setOpenSettings] = useState(false);
  const [openProfile, setOpenProfile] = useState(false);
  const route = useLocation().pathname.split("/").slice(1);
  //In order to not print the ID
  route[route.length - 2] &&
    (route[route.length - 2] === "alarm-details" ||
      route[route.length - 2] === "cases") &&
    route.pop();

  const tlmGetLanguagesAPI = async () => {
    try {
      const restOperation = get({
        apiName: "TelemonitoringAPI",
        path: "/languages",
        options: {
          body: {},
          headers: {
            "Content-Type": "application/json",
            Authorization: `${await currentUserToken()}`,
          },
        },
      });
      const { body } = await restOperation.response;
      const response = await body.json();

      setLanguages(response);
      dispatchLanguage({ type: "DATA_RECEIVED", value: response });
    } catch (error) {
      console.error("Error fetching languages:", error);
    }
  };

  useEffect(() => {
    if (refreshLanguages) {
      refreshLanguages(tlmGetLanguagesAPI); // Pass the function to be triggered externally
    }
    tlmGetLanguagesAPI();
  }, [refreshLanguages]);
  // Imposta la modalità iniziale basata sul valore in cache o su Light Mode come default
  useEffect(() => {
    const savedDarkMode = localStorage.getItem("darkMode") === "true";
    setDarkMode(dispatch, savedDarkMode);
    setDarkSidenav(dispatch, savedDarkMode);
  }, [dispatch]);
  useEffect(() => {
    if (fixedNavbar) {
      setNavbarType("sticky");
    } else {
      setNavbarType("static");
    }
    function handleTransparentNavbar() {
      setTransparentNavbar(
        dispatch,
        (fixedNavbar && window.scrollY === 0) || !fixedNavbar
      );
    }
    window.addEventListener("scroll", handleTransparentNavbar);
    handleTransparentNavbar();
    return () => window.removeEventListener("scroll", handleTransparentNavbar);
  }, [dispatch, fixedNavbar]);

  const handleDarkMode = () => {
    const newDarkMode = !darkMode;
    setDarkSidenav(dispatch, newDarkMode);
    setDarkMode(dispatch, newDarkMode);
    localStorage.setItem("darkMode", newDarkMode);
  };

  const handleOpenSettings = (event) => setOpenSettings(event.currentTarget);
  const handleCloseSettings = () => setOpenSettings(false);

  const handleOpenProfile = (event) => setOpenProfile(event.currentTarget);
  const handleCloseProfile = () => setOpenProfile(false);

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue); // Imposta immediatamente il valore del tab
    // Verifica che il tabValue venga aggiornato correttamente
    if (newValue !== tabValue) {
    }
    switch (newValue) {
      case 0: // Alarms tab
        navigate("/dashboard"); // Naviga a /dashboard
        break;
      case 1:
        navigate("/stores"); // Aggiungi il percorso per la seconda tab
        break;
      case 2:
        navigate("/cases"); // Aggiungi il percorso per la terza tab
        break;
      case 3: // Settings tab
        navigate("/settings"); // Naviga a /tables
        break;
      case 4: // Settings tab
        navigate("/profile"); // Naviga a /tables
        break;
      default:
        break;
    }
  };
  useEffect(() => {
    const currentPath = window.location.pathname;
    if (currentPath.includes("/dashboard")) {
      setTabValue(0);
    } else if (currentPath.includes("/stores")) {
      setTabValue(1);
    } else if (currentPath.includes("/cases")) {
      setTabValue(2);
    } else if (currentPath.includes("/settings")) {
      setTabValue(3);
    } else if (currentPath.includes("/profile")) {
      setTabValue(4);
    }
  }, []);
  const handleLanguageChange = (event) => {
    const selectedLanguage = event.target.value;
    setLanguage(selectedLanguage);
    I18n.setLanguage(selectedLanguage);
    localStorage.setItem("language", selectedLanguage);
    window.location.reload(); // Ricarica la pagina per applicare la nuova lingua
  };

  const renderSettings = () => (
    <Menu
      anchorEl={openSettings}
      anchorReference={null}
      anchorOrigin={{
        vertical: "bottom",
        horizontal: "left",
      }}
      open={Boolean(openSettings)}
      onClose={handleCloseSettings}
      sx={{ mt: 2 }}
    >
      <MenuItem sx={{mb:1}}>
        <FormControl
          key={"Language"}
          margin="dense"
          sx={{
            minWidth: "10rem",
          }}
        >
          <InputLabel>{I18n.get("Language")}</InputLabel>
          <Select
            label={I18n.get("Language")}
            value={language || ""}
            onChange={handleLanguageChange}
            startAdornment={<LanguageIcon sx={{ mr: 1 }} />}
          >
            {languages.map((lang) => (
              <MenuItem key={lang} value={lang.slice(0, 2).toLowerCase()}>
                {I18n.get(lang)}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </MenuItem>
      
      <MenuItem>
        <TemplateBox
          display="flex"
          justifyContent="space-between"
          lineHeight={1}
        >
          <Stack
            alignItems="center"
            justifyContent="center"
            direction="row"
            spacing={1}
          >
            <TemplateTypography variant="h6">
              {I18n.get("Light / Dark")}
            </TemplateTypography>
            <Switch checked={darkMode} onChange={handleDarkMode} />
          </Stack>
        </TemplateBox>
      </MenuItem>
    </Menu>
  );

  const renderProfile = () => (
    <Menu
      anchorEl={openProfile}
      anchorReference={null}
      anchorOrigin={{
        vertical: "bottom",
        horizontal: "left",
      }}
      open={Boolean(openProfile)}
      onClose={handleCloseProfile}
      sx={{ mt: 2 }}
    >
      <TemplateBox sx={{ pl: 1 }}>
        <b> {I18n.get("Account Information")} </b> :
      </TemplateBox>
      <MenuItem></MenuItem>
      <MenuItem>
        <Box sx={{ pr: 1 }}>
          <Avatar {...stringAvatar(userInfo.name || "No name found")} />
        </Box>
        {userInfo.name || "No name found"}
      </MenuItem>
      <MenuItem>
        <Box sx={{ pr: 1 }}>
          <Avatar
            sx={{
              bgcolor: stringToColor(userInfo.name),
              width: 35,
              height: 35,
            }}
          >
            <GroupOutlinedIcon />
          </Avatar>
        </Box>
        {userInfo.profiles && userInfo.profiles.length > 0
          ? userInfo.profiles[0]
          : "No profile found"}
      </MenuItem>
      <MenuItem>
        <Box sx={{ pr: 1 }}>
          <Avatar
            sx={{
              bgcolor: stringToColor(userInfo.name),
              width: 35,
              height: 35,
            }}
          >
            <MailOutlineIcon />
          </Avatar>
        </Box>
        {userInfo.email || "No email found"}
      </MenuItem>
    </Menu>
  );

  const [emailNotifications, setEmailNotifications] = useState(0);

  const tlmGetUnrecognisedEmailsNumber = async() => {
    try {
      const restOperation = get({
        apiName: "TelemonitoringAPI",
        path: "/unrecognisedEmails/number/get",
        options: {
          headers: {
            "Content-Type": "application/json",
            Authorization: `${await currentUserToken()}`,
          },
        },
      });
      const { body } = await restOperation.response;
      const response = await body.json();
      const numUnrecEmails = await response[0]?.num_of_unrec_emails;
      setEmailNotifications(numUnrecEmails);

      //dispatch({ type: "DATA_RECEIVED", value: response });
    } catch (error) {
      console.error("Error during the API call: ", error);
    }
  };

  useEffect(() => {
    tlmGetUnrecognisedEmailsNumber();
  }, []);

  return (
    <TemplateBox position="relative">
      <AppBar
        position={absolute ? "absolute" : navbarType}
        color="inherit"
        sx={(theme) => navbar(theme, { transparentNavbar, absolute, light })}
      >
        <Toolbar sx={(theme) => navbarContainer(theme, { navbarType })}>
          <TemplateBox
            color={light && transparentNavbar ? "white" : "dark"}
            mb={{ xs: 1, md: 0 }}
            sx={(theme) => navbarRow(theme, { isMini })}
          >
            <Stack direction="row" spacing={4}>
              <img
                src={brand}
                alt="Logo"
                style={{
                  width: "45px",
                  height: "45px",
                  marginTop: -3,
                  marginLeft: -10,
                }}
              />

              <Breadcrumbs
                icon="home"
                title={I18n.get(route[route.length - 1])} // Traduzione del titolo
                route={route}
                light={transparentNavbar ? light : false}
              />
            </Stack>
          </TemplateBox>
          {!mobileDevice && (
            <TemplateBox sx={{ width: "500px" }}>
              <Tabs value={tabValue} onChange={handleTabChange}>
                <Tab
                  key={0}
                  label={I18n.get("Alarms")}
                  icon={<WarningIcon />}
                />
                <Tab
                  key={1}
                  label={I18n.get("Stores")}
                  icon={<StorefrontIcon />}
                />
                <Tab key={2} label={I18n.get("Cases")} icon={<MonitorIcon />} />
                {userInfo.profiles[0].includes("NOT ADMIN") ? (
                  <Tab
                    key={3}
                    label={I18n.get("Settings")}
                    icon={<SettingsIcon />}
                    disabled
                  />
                ) : (
                  <Tab
                    key={3}
                    label={I18n.get("Settings")}
                    icon={<SettingsIcon />}
                    disabled={
                      !(
                        userInfo.profiles[0].includes("SuperAdmin") ||
                        userInfo.profiles[0].includes("Admin")
                      )
                    }
                  />
                )}
                {userInfo.profiles[0].includes("NOT ADMIN") ? (
                  <Tab
                    key={4}
                    label={I18n.get("Users")}
                    icon={<PersonIcon />}
                    disabled
                  />
                ) : (
                  <Tab
                    key={4}
                    label={I18n.get("Users")}
                    icon={<PersonIcon />}
                    disabled={
                      !(
                        userInfo.profiles[0].includes("SuperAdmin") ||
                        userInfo.profiles[0].includes("Admin")
                      )
                    }
                  />
                )}
              </Tabs>
            </TemplateBox>
          )}

          {isMini ? null : (
            <TemplateBox sx={(theme) => navbarRow(theme, { isMini })}>
              <TemplateBox color={light ? "white" : "inherit"}>
                {
                  (
                    userInfo.profiles[0].includes("SuperAdmin") ||
                    userInfo.profiles[0].includes("Admin")
                  ) ?
                  <Tooltip
                    title={I18n.get("Unrecognized Emails") + " " + emailNotifications}
                    placement="top"
                    sx={{ cursor: "pointer" }}
                  >
                    <Badge 
                      badgeContent={emailNotifications} 
                      color={"primary"} 
                      max={999}
                    >
                    <IconButton
                      size="small"
                      color={light && transparentNavbar ? "white" : "dark"}
                      sx={navbarIconButton}
                      onClick={() => navigate("/settings/unrecognized")}
                    >
                      <NotificationsIcon>unrecognised Emails</NotificationsIcon>
                    </IconButton>
                    </Badge>
                  </Tooltip>
                : ""}
                <Tooltip
                  title={I18n.get("Account Information")}
                  placement="top"
                  sx={{ cursor: "pointer" }}
                >
                  <IconButton
                    size="small"
                    color={light && transparentNavbar ? "white" : "dark"}
                    sx={navbarIconButton}
                    onClick={handleOpenProfile}
                  >
                    <Icon>account_circle</Icon>
                  </IconButton>
                </Tooltip>
                <Tooltip
                  title={I18n.get("Settings")}
                  placement="top"
                  sx={{ cursor: "pointer" }}
                >
                  <IconButton
                    size="small"
                    color={light && transparentNavbar ? "white" : "dark"}
                    sx={navbarIconButton}
                    onClick={handleOpenSettings}
                  >
                    <Icon>settings</Icon>
                  </IconButton>
                </Tooltip>
                {renderSettings()}
                {renderProfile()}
                <Tooltip
                  title={I18n.get("Sign Out")}
                  placement="top"
                  sx={{ cursor: "pointer" }}
                >
                  <IconButton
                    size="small"
                    color={light && transparentNavbar ? "white" : "dark"}
                    sx={navbarIconButton}
                    onClick={() => signOut()}
                  >
                    {/* <TemplateTypography
                    variant="button"
                    fontWeight="medium"
                    color={light && transparentNavbar ? "white" : "dark"}
                  >
                    {I18n.get("Sign Out")}
                  </TemplateTypography> */}
                    <Icon>logout</Icon>
                  </IconButton>
                </Tooltip>
              </TemplateBox>
            </TemplateBox>
          )}
        </Toolbar>
        {mobileDevice && (
          <>
            <IconButton
              edge="start"
              color="inherit"
              aria-label="menu"
              sx={{ width: 25, height: 25, ml: 2 }}
              onClick={handleDrawerToggle}
            >
              <Icon>menu</Icon>
            </IconButton>
            <Drawer
              anchor="left"
              open={drawerOpen}
              onClose={handleDrawerToggle}
            >
              <TemplateBox sx={{ height: "40%" }}>
                <Tabs
                  sx={{ height: "100%" }}
                  orientation="vertical"
                  value={tabValue}
                  onChange={handleTabChange}
                >
                  <Tab
                    key={0}
                    label={I18n.get("Alarms")}
                    icon={<WarningIcon />}
                  />
                  <Tab
                    key={1}
                    label={I18n.get("Stores")}
                    icon={<StorefrontIcon />}
                  />
                  <Tab
                    key={2}
                    label={I18n.get("Cases")}
                    icon={<MonitorIcon />}
                  />
                  {userInfo.profiles[0].includes("NOT ADMIN") ? (
                    <Tab
                      key={3}
                      label={I18n.get("Settings")}
                      icon={<SettingsIcon />}
                      disabled
                    />
                  ) : (
                    <Tab
                      key={3}
                      label={I18n.get("Settings")}
                      icon={<SettingsIcon />}
                      disabled={
                        !(
                          userInfo.profiles[0].includes("SuperAdmin") ||
                          userInfo.profiles[0].includes("Admin")
                        )
                      }
                    />
                  )}
                  {userInfo.profiles[0].includes("NOT ADMIN") ? (
                    <Tab
                      key={4}
                      label={I18n.get("Users")}
                      icon={<PersonIcon />}
                      disabled
                    />
                  ) : (
                    <Tab
                      key={4}
                      label={I18n.get("Users")}
                      icon={<PersonIcon />}
                      disabled={
                        !(
                          userInfo.profiles[0].includes("SuperAdmin") ||
                          userInfo.profiles[0].includes("Admin")
                        )
                      }
                    />
                  )}
                </Tabs>
              </TemplateBox>
            </Drawer>
          </>
        )}
      </AppBar>
      <TemplateBox height={mobileDevice ? "120px" : "70px"} />
    </TemplateBox>
  );
}
// Setting default values for the props of DashboardNavbar
DashboardNavbar.defaultProps = {
  absolute: false,
  light: true,
  isMini: false,
};
// Typechecking props for the DashboardNavbar
DashboardNavbar.propTypes = {
  absolute: PropTypes.bool,
  light: PropTypes.bool,
  isMini: PropTypes.bool,
  toggleDarkMode: PropTypes.func.isRequired,
};

export default DashboardNavbar;
